import React from 'react'
import { object } from 'prop-types'
import styled, { withTheme } from 'styled-components'
import LazyLoad from 'react-lazyload'
import { rem } from 'polished'
import { Flex, Box } from 'rebass'
import citygateTheme from '../../theme/'
import ReactMapboxGl, { Marker } from 'react-mapbox-gl'
import { lazyload, media } from '../../utils/style'
import IconMapPin from '../../vectors/IconMapPin'
import buildImageUrl from '../../../lib/media-url-maker'

import 'mapbox-gl/dist/mapbox-gl.css'

const FlexWrapper = styled(({ mapIsDisplayed, ...rest }) => <Flex {...rest} />)`
  position: relative;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  ${media.MediumScreenUp`
    max-width: 79%;
  `};
  ${media.LargeScreenUp`
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 40%;
    left: -50%;
    width: 200vw;
    height: 70%;
    background-color: ${(props) => props.theme.colors.blue};
    z-index: 0;
  }
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${(props) => (props.mapIsDisplayed ? '100%' : '50%')};
    height: 89.5%;
    box-shadow: 0 30px 30px 0px rgba(66, 76, 94, 0.05),
                0 25px 25px 0 rgba(0, 0, 0, 0.03);
  }
  `};
`

const BusinessMapLocation = styled(Box)`
  position: relative;
  z-index: 1;
`

const BusinessHours = styled(Box)`
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${(props) => props.theme.colors.white};
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${rem(56)};
    background-color: ${(props) => props.theme.colors.blue};
    z-index: 0;
  }
  table {
    position: relative;
    z-index: 1;
  }
  tr {
    border-color: ${(props) => props.theme.colors.grey_light_op30};
  }
  th {
    text-transform: uppercase;
    font-size: ${(props) => rem(props.theme.fontSizes[1])};
    font-weight: ${(props) => props.theme.weights[1]};
    color: ${(props) => props.theme.colors.white};
    background-color: ${(props) => props.theme.colors.blue};
    padding: ${rem(10)} 0;
    margin-bottom: ${rem(20)};
  }
  td {
    color: ${(props) => props.theme.colors.grey_light};
    font-size: ${rem(14)};
    padding: ${rem(10)} 0;
    white-space: nowrap;
  }
  td:first-child {
    font-weight: ${(props) => props.theme.weights[1]};
    ${media.MediumScreenUp`
    width: 50%;
    `};
  }
  th:last-child,
  td:last-child {
    ${media.MediumScreenUp`
    text-align: right;
    `};
  }
  tr:first-child th,
  tr:first-child td,
  tr:last-child td {
    border-bottom: none;
  }
  tr:nth-child(2) td {
    padding-top: ${rem(35)};
  }
`

const BusinessHoursTable = ({ items, disableVehicleAllocating }) => (
  <table>
    <tbody>
      <tr>
        <th>Business Hours</th>
        {!disableVehicleAllocating && <th>Sales</th>}
        <th>Service</th>
      </tr>
      {items.map((item, i) => (
        <tr key={`business-hours-row-${i}`}>
          <td>{item.label}</td>
          {!disableVehicleAllocating && <td>{item.sales || 'Closed'}</td>}
          <td>{item.service || 'Closed'}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const HoverMarker = styled(Marker || Box)`
  cursor: pointer;
`

const formatBusinessHours = (dealership) => {
  const schedule = []
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ]

  days.forEach((day) => {
    const serviceKey = `${day}ServiceHours`
    const salesKey = `${day}SalesHours`

    const scheduleRow = {
      day,
      label: `${day.charAt(0).toUpperCase()}${day.slice(1)}`,
      sales: dealership[salesKey],
      service: dealership[serviceKey]
    }
    schedule.push(scheduleRow)
  })
  return schedule
}

const _isWindowAvailable = typeof window !== 'undefined'
const Map = _isWindowAvailable
  ? ReactMapboxGl({
      accessToken: `${process.env.MAPBOX_ACCESS_TOKEN}`,
      scrollZoom: false
    })
  : null

class ContentDealershipLocationDetails extends React.PureComponent {
  render() {
    const { long, lat, mapsUrl, mapImage } = this.props.dealership
    const mapImageUrl = mapImage && buildImageUrl({ resource: mapImage[0] })
    const coordinates = [+long, +lat]
    const overrideMap = mapImage && mapImage.length !== 0

    const url =
      mapsUrl ||
      `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
    const onMapClick = () => window.open(url, '_blank')

    return (
      <FlexWrapper
        pb={[0, null, 60]}
        flexWrap="wrap"
        mapIsDisplayed={lat && long}
      >
        <BusinessHours width={[1 / 1, null, 1 / 2]} px={[20, '3%']}>
          <BusinessHoursTable
            items={formatBusinessHours(this.props.dealership)}
            disableVehicleAllocating={
              this.props.dealership.disableVehicleAllocating
            }
          />
        </BusinessHours>
        <BusinessMapLocation
          width={[1 / 1, null, 1 / 2]}
          bg={citygateTheme.colors.white}
          mb={[-28, 60, 0]}
        >
          {_isWindowAvailable && lat && long && !overrideMap && (
            <LazyLoad offset={lazyload.offset} height={505} once>
              <Map
                center={coordinates}
                style="mapbox://styles/citygateauto/cjeifb30x7khd2rp16lz1lbap"
                containerStyle={{
                  height: '505px',
                  width: '100%'
                }}
              >
                <HoverMarker
                  onClick={onMapClick}
                  coordinates={coordinates}
                  anchor="bottom"
                >
                  <IconMapPin />
                </HoverMarker>
              </Map>
            </LazyLoad>
          )}
          {overrideMap && <img src={mapImageUrl} onClick={onMapClick} />}
        </BusinessMapLocation>
      </FlexWrapper>
    )
  }
}

ContentDealershipLocationDetails.defaultProps = {
  dealership: null
}
ContentDealershipLocationDetails.propTypes = {
  dealership: object
}

export default withTheme(ContentDealershipLocationDetails)
